import { useState, useMemo, useEffect, useCallback } from "react";
import {
  AnimatePresence,
  motion,
  MotionConfig,
  useReducedMotion,
} from "framer-motion";
import { loadNarrativeFonts, narrativeFontStyle } from "../utils/googleFonts";
import { NarrativeSection } from "./NarrativeSection";
import { ProgressBars } from "./ProgressBars";
import { AnimatedBackground } from "./AnimatedBackground";
// import { getSectionThemeColor } from "../utils/colorPalettes";
import { BackgroundMedia } from "./BackgroundMedia";
import { AudioControlButton } from "./AudioControlButton";
import { SectionInterstitial } from "./SectionInterstitial";
import { HeaderSectionLabel } from "./HeaderSectionLabel";
import { ContinueIndicator } from "./ContinueIndicator";
import { AnimationOverlays } from "./AnimationOverlays";
import { PinnedBookmarkButton } from "./PinnedBookmarkButton";
import { BookmarksOverlay } from "./BookmarksOverlay";
import { ANIMATION_DURATIONS } from "../constants/animations";
import type { NewNarrativeData, DataValues } from "../types/narrative";
import { useBackgroundMusic } from "../hooks/useBackgroundMusic";
import { useAudioContext } from "../hooks/useAudioContext";
import { useNarrativeStructure } from "../hooks/useNarrativeStructure";
import { useNarrativeNavigation } from "../hooks/useNarrativeNavigation";
import { useGroupInteractionStatus } from "../hooks/useGroupInteractionStatus";
import { useSectionTransition } from "../hooks/useSectionTransition";
import { useDocumentMeta } from "../hooks/useDocumentMeta";

interface NarrativeViewerProps {
  narrativeData: NewNarrativeData;
  dataValues: DataValues;
  selectedSections?: Set<string>;
  onSectionToggle?: (sectionId: string, isSelected: boolean) => void;
  initialGroupIndex?: number;
  onGroupIndexChange?: (index: number) => void;
  format?: string | null;
  videoDelayMs?: number | null;
}

export function NarrativeViewer({
  narrativeData,
  dataValues,
  selectedSections,
  onSectionToggle,
  initialGroupIndex = 0,
  onGroupIndexChange,
  format,
  videoDelayMs,
}: NarrativeViewerProps) {
  // 1. Structural Data
  const {
    visibleSectionIds,
    visibleSections,
    totalGroups,
    sectionMap,
    getSectionInfoForGroup,
  } = useNarrativeStructure(narrativeData, selectedSections);

  // 2. Navigation State & Scroll Engine
  const {
    currentGroupIndex,
    isNavigatingBackward,
    scrollContainerRef,
    scrollToIndex,
    advanceGroup,
    handleScroll,
    showProgress,
    groupAnimationStates,
    handleGroupAnimationComplete,
  } = useNarrativeNavigation(
    totalGroups,
    initialGroupIndex,
    sectionMap,
    visibleSectionIds,
    onGroupIndexChange,
  );

  // 3. Derived Current Section Info
  const startGroupInfo = useMemo(
    () => getSectionInfoForGroup(currentGroupIndex),
    [currentGroupIndex, getSectionInfoForGroup],
  );
  const currentSectionId =
    startGroupInfo?.sectionId || sectionMap[0]?.sectionId;

  const currentSection = useMemo(
    () => visibleSections.find((s) => s.id === currentSectionId),
    [visibleSections, currentSectionId],
  );

  const currentBackgroundMusic =
    typeof currentSection?.backgroundMusic === "object"
      ? currentSection.backgroundMusic.src
      : currentSection?.backgroundMusic;
  const currentBackgroundMusicVolume =
    typeof currentSection?.backgroundMusic === "object"
      ? (currentSection.backgroundMusic.volume ?? 0.3)
      : 0.3;
  const currentBackgroundMusicLoop =
    typeof currentSection?.backgroundMusic === "object"
      ? (currentSection.backgroundMusic.loop ?? true)
      : true;
  const currentBackgroundMedia = currentSection?.backgroundMedia;

  // Determine whether bookmark and music controls should be shown.
  // These are derived from the full narrative (all sections), not just the
  // currently visible ones, so the icons don't disappear when sections are
  // toggled via the table of contents.
  const hasBookmarkItems = useMemo(
    () =>
      narrativeData.sections.some((section) =>
        section.groups.some((group) =>
          group.items.some((item) => item.type === "cta_bookmark"),
        ),
      ),
    [narrativeData.sections],
  );

  const hasMusic = useMemo(
    () =>
      narrativeData.sections.some((section) =>
        Boolean(section.backgroundMusic),
      ),
    [narrativeData.sections],
  );
  const currentSectionOverlay = currentSection?.overlay;
  const currentSectionLabel = currentSection?.label || "";
  const currentSectionIcon = currentSection?.icon;

  // 4. Interaction Status (Quiz, Buttons)
  const { currentGroupHasInteractiveElements, currentGroupQuizCompleted } =
    useGroupInteractionStatus(currentGroupIndex, sectionMap, visibleSections);

  // 5. Video Mode & No Animation Logic (needed early for section transitions)
  const isVideoMode = format === "video";
  const noAnimation =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("noAnimation") === "true";
  const prefersReducedMotion = useReducedMotion();

  // 6. Section Transitions & Interstitials
  const { interstitial, contentDelayed } = useSectionTransition(
    currentSectionId,
    visibleSections,
    // Disable interstitials in video mode, PDF export, and under reduced motion
    // (the interstitial is a timed dwell + transition — i.e. motion).
    isVideoMode || noAnimation || Boolean(prefersReducedMotion),
  );

  // 7. Document Meta (page title based on narrative)
  useDocumentMeta(narrativeData.metadata);

  // 8. Custom font loading — inject Google Fonts <link> when narrative specifies fonts
  const { header_font, body_font } = narrativeData.metadata;
  useEffect(() => {
    loadNarrativeFonts(header_font, body_font);
  }, [header_font, body_font]);

  // 6. UI Interaction & Audio
  const [showBookmarksOverlay, setShowBookmarksOverlay] = useState(false);
  const { isMuted, setIsMuted, toggleMute } = useAudioContext();

  const { unmute, mute } = useBackgroundMusic({
    trackPath: currentBackgroundMusic,
    volume: currentBackgroundMusicVolume,
    loop: currentBackgroundMusicLoop,
    fadeDuration: 2000,
    enabled: true,
  });

  const handleMuteToggle = () => {
    if (isMuted) {
      unmute(currentBackgroundMusicVolume);
    } else {
      mute();
    }
    toggleMute();
  };

  // Called when a music_choice item's "Play with Music" button is clicked.
  // Unmutes audio and marks the context as unmuted so the toggle button reflects the state.
  const handlePlayWithMusic = () => {
    unmute(currentBackgroundMusicVolume);
    setIsMuted(false);
  };

  // Called when a music_choice item's "Continue without" button is clicked.
  // Mutes audio and marks the context as muted.
  const handleMuteMusic = () => {
    mute();
    setIsMuted(true);
  };

  // Hide the audio toggle button when the current group contains a music_choice item
  const currentGroup = useMemo(() => {
    if (!startGroupInfo || !currentSection) return undefined;
    const localIndex = currentGroupIndex - startGroupInfo.startGroupIndex;
    return currentSection.groups[localIndex];
  }, [currentGroupIndex, startGroupInfo, currentSection]);

  const currentGroupHasMusicChoice = useMemo(() => {
    return (
      currentGroup?.items.some((item) => item.type === "music_choice") ?? false
    );
  }, [currentGroup]);

  // Update theme-color meta tag (Safari status bar and iOS overscroll areas)
  // DISABLED: Keeping status bar fixed at black for now
  // useEffect(() => {
  //   if (currentSectionId) {
  //     const themeColor = currentBackgroundMedia
  //       ? "#000000"
  //       : getSectionThemeColor(currentSectionId, currentSection?.colorPalette);
  //     const metaThemeColor = document.querySelector('meta[name="theme-color"]');
  //     if (metaThemeColor) {
  //       metaThemeColor.setAttribute("content", themeColor);
  //     }
  //     document.documentElement.style.background = themeColor;
  //     document.body.style.background = themeColor;
  //   }
  // }, [currentSectionId, currentBackgroundMedia, currentSection?.colorPalette]);

  // Handle continue button click from TOC
  const handleContinue = () => {
    if (!selectedSections || selectedSections.size === 0) return;

    // Check if the TOC is in read-only mode (no dedicated table_of_contents section)
    // In that case, just advance to the next page
    const hasTOCSection = narrativeData.sections.some(
      (s) => s.id === "table_of_contents",
    );
    if (!hasTOCSection) {
      if (currentGroupIndex < totalGroups - 1) {
        scrollToIndex(currentGroupIndex + 1);
      }
      return;
    }

    const firstSelectedSection = sectionMap.find(
      (section) =>
        section.sectionId !== "intro" &&
        section.sectionId !== "table_of_contents" &&
        selectedSections.has(section.sectionId),
    );

    if (firstSelectedSection) {
      scrollToIndex(firstSelectedSection.startGroupIndex);
    }
  };

  // Handle reaction selection
  const handleReactionSelect = () => {
    if (currentGroupIndex < totalGroups - 1) {
      scrollToIndex(currentGroupIndex + 1, true);
    }
  };

  // 7. Video Mode UI State
  const [isUiVisible, setIsUiVisible] = useState(!isVideoMode);
  const [lastInteraction, setLastInteraction] = useState(Date.now());

  // Auto-advance: group-level autoAdvance advances 3s after animation completes;
  // video mode uses 5s delay for groups without autoAdvance, 3s for groups with it
  const groupAutoAdvance = currentGroup?.autoAdvance === true;

  useEffect(() => {
    if (!isVideoMode && !groupAutoAdvance) return;

    if (groupAnimationStates[currentGroupIndex]) {
      const delay = videoDelayMs ?? (groupAutoAdvance ? 3000 : 5000);
      const timer = setTimeout(() => {
        const nextIndex = (currentGroupIndex + 1) % totalGroups;
        scrollToIndex(nextIndex, true);
      }, delay);
      return () => clearTimeout(timer);
    }
  }, [
    isVideoMode,
    currentGroupIndex,
    groupAnimationStates,
    groupAutoAdvance,
    videoDelayMs,
    totalGroups,
    scrollToIndex,
  ]);

  // UI Visibility
  useEffect(() => {
    if (!isVideoMode) return;

    setIsUiVisible(true);
    const timer = setTimeout(() => setIsUiVisible(false), 3000);
    return () => clearTimeout(timer);
  }, [isVideoMode, lastInteraction]);

  const handleInteraction = useCallback(() => {
    if (isVideoMode) setLastInteraction(Date.now());
  }, [isVideoMode]);

  // Attach global listeners
  useEffect(() => {
    if (!isVideoMode) return;

    window.addEventListener("mousemove", handleInteraction);
    window.addEventListener("touchstart", handleInteraction);
    window.addEventListener("click", handleInteraction);
    return () => {
      window.removeEventListener("mousemove", handleInteraction);
      window.removeEventListener("touchstart", handleInteraction);
      window.removeEventListener("click", handleInteraction);
    };
  }, [isVideoMode, handleInteraction]);

  const handleScrollWrapper = () => {
    handleScroll();
  };

  // Prevent overscroll bounce only while narrative is mounted
  useEffect(() => {
    document.body.classList.add("narrative-active");
    return () => document.body.classList.remove("narrative-active");
  }, []);

  // Helper for conditional classes
  const uiVisibilityClass = isVideoMode
    ? `transition-opacity duration-500 ${isUiVisible ? "opacity-100" : "opacity-0 pointer-events-none"}`
    : "";
  // Video mode fades the chrome out but leaves it mounted. `pointer-events-none`
  // on a wrapper is not enough for a subtree that re-enables itself with
  // `pointer-events-auto` (the progress rows do); `inert` disables the whole
  // subtree — hit testing, focus and the a11y tree — so faded-out controls
  // cannot be clicked or tabbed into.
  const isUiHidden = isVideoMode && !isUiVisible;

  // Get the current section's color palette (if defined inline)
  const currentSectionPalette = currentSection?.colorPalette;

  // Expose animation state for external tools (e.g., screenshot capture scripts)
  const isAnimationComplete = groupAnimationStates[currentGroupIndex] === true;

  // CSS custom property overrides for custom narrative fonts (scoped to this container)
  const fontStyle = narrativeFontStyle(header_font, body_font);

  return (
    <MotionConfig reducedMotion="user">
      <div
        className="relative h-full w-full overflow-hidden"
        style={fontStyle}
        data-group-index={currentGroupIndex}
        data-animation-complete={isAnimationComplete}
      >
        {/* Background gradient */}
        <AnimatePresence>
          <motion.div
            key={`gradient-${currentSectionId}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              duration: ANIMATION_DURATIONS.gradientTransition / 1000,
              ease: "easeInOut",
            }}
            className="absolute inset-0 z-0"
          >
            <AnimatedBackground
              sectionId={currentSectionId}
              palette={currentSectionPalette}
            />
          </motion.div>
        </AnimatePresence>

        {/* Background media layer */}
        {currentBackgroundMedia && (
          <AnimatePresence>
            <motion.div
              key={`media-${currentSectionId}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{
                duration: ANIMATION_DURATIONS.gradientTransition / 1000,
                ease: "easeInOut",
              }}
              className="absolute inset-0 z-1"
            >
              <BackgroundMedia
                media={currentBackgroundMedia}
                sectionId={currentSectionId}
              />
            </motion.div>
          </AnimatePresence>
        )}

        {/* Section-level animation overlay(s) - separate layer, doesn't affect scroll */}
        {currentSectionOverlay && (
          <div className="absolute inset-0 overflow-hidden pointer-events-none z-10">
            <AnimationOverlays
              key={`section-overlay-${currentSectionId}`}
              overlay={currentSectionOverlay}
              keyPrefix={`section-${currentSectionId}`}
            />
          </div>
        )}

        {/* Customer header */}
        {!interstitial && (
          <div
            className={`absolute top-0 left-0 right-0 z-50 backdrop-blur-sm ${uiVisibilityClass}`}
          >
            <div className="relative px-0 py-4">
              <div
                className="text-center cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => scrollToIndex(0)}
              >
                <p
                  className="text-white/50 text-xs tracking-widest uppercase"
                  style={{
                    fontFamily: "var(--font-narrative-body)",
                    fontWeight:
                      "var(--font-narrative-body-weight)" as unknown as number,
                  }}
                >
                  {narrativeData.metadata.customer_name}
                </p>
                <HeaderSectionLabel
                  sectionId={currentSectionId}
                  label={currentSectionLabel}
                  icon={currentSectionIcon}
                />
              </div>
              {/* Audio toggle button */}
              {hasMusic && !currentGroupHasMusicChoice && (
                <div
                  className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-auto"
                  data-audio-control
                >
                  <AudioControlButton
                    isPlaying={!isMuted}
                    onToggle={handleMuteToggle}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Scrollable container with snap points */}
        <div
          ref={scrollContainerRef}
          onScroll={handleScrollWrapper}
          role="region"
          aria-label="Narrative content"
          tabIndex={0}
          className="h-full w-full overflow-y-scroll overflow-x-hidden snap-y snap-mandatory scrollbar-hide relative z-20 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-white/60"
          style={{
            scrollBehavior: "smooth",
            WebkitOverflowScrolling: "touch",
          }}
        >
          {visibleSections.map((section, sectionIndex) => {
            const sectionInfo = sectionMap[sectionIndex];
            return (
              <NarrativeSection
                key={section.id}
                section={section}
                data={dataValues}
                startGroupIndex={sectionInfo.startGroupIndex}
                currentGroupIndex={currentGroupIndex}
                onGroupAnimationComplete={handleGroupAnimationComplete}
                onReactionSelect={handleReactionSelect}
                selectedSections={selectedSections}
                onSectionToggle={onSectionToggle}
                onContinue={handleContinue}
                contentDelayed={contentDelayed}
                isNavigatingBackward={isNavigatingBackward}
                narrativeData={narrativeData}
                metadata={narrativeData.metadata}
                format={format}
                noAnimation={noAnimation}
                onPlayWithMusic={handlePlayWithMusic}
                onMuteMusic={handleMuteMusic}
              />
            );
          })}
        </div>

        {/* Progress dots - bars always visible on desktop; the labels and
            icons fade in on hover, keyboard focus, or backward navigation.
            The region is only a positioning anchor: it is `pointer-events-none`
            so it never sits over the narrative, and `ProgressBars` re-enables
            input on the section rows alone. Hover lives in CSS there, so
            there is no hover state to keep in sync here. */}
        <div className={uiVisibilityClass} inert={isUiHidden}>
          <div
            data-progress-menu-region
            className="hidden sm:block fixed right-0 top-1/2 z-30 -translate-y-1/2 pointer-events-none"
          >
            <ProgressBars
              sections={sectionMap.map((s) => ({
                ...s,
                label: visibleSections.find((vs) => vs.id === s.sectionId)
                  ?.label,
                icon: visibleSections.find((vs) => vs.id === s.sectionId)?.icon,
              }))}
              currentGroupIndex={currentGroupIndex}
              onDotClick={scrollToIndex}
              showLabels={showProgress}
            />
          </div>
        </div>

        {/* Section interstitial */}
        <AnimatePresence>
          {interstitial && (
            <SectionInterstitial
              key={interstitial.sectionId}
              sectionLabel={interstitial.label}
              icon={interstitial.icon}
            />
          )}
        </AnimatePresence>

        {/* Continue indicator */}
        <div
          className={`absolute bottom-2 left-0 right-0 z-30 flex justify-center pointer-events-none ${uiVisibilityClass}`}
          data-continue-indicator
        >
          <AnimatePresence>
            <ContinueIndicator
              onContinue={advanceGroup}
              groupIndex={currentGroupIndex}
              show={
                currentGroupHasInteractiveElements === false &&
                currentGroupQuizCompleted === true &&
                groupAnimationStates[currentGroupIndex] === true &&
                currentGroupIndex < totalGroups - 1
              }
            />
          </AnimatePresence>
        </div>

        {/* Pinned bookmark button */}
        {!interstitial && hasBookmarkItems && (
          <div
            className={`fixed bottom-4 right-4 z-50 ${uiVisibilityClass}`}
            data-bookmark-button
          >
            <PinnedBookmarkButton
              onClick={() => setShowBookmarksOverlay(true)}
            />
          </div>
        )}

        {/* Bookmarks overlay */}
        <BookmarksOverlay
          narrativeData={narrativeData}
          isOpen={showBookmarksOverlay}
          onClose={() => setShowBookmarksOverlay(false)}
        />
      </div>
    </MotionConfig>
  );
}
